import {
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router';
import { uniqBy } from 'lodash';

import {
  ActionsData,
  FileTreeItemType,
  RyeconColorDbt,
  TreeNodeValues,
  useOpenDirectories,
} from '@dbt-labs/sourdough';

import { FileAppliedStateNode } from '@/__generated__/graphql';
import { useLinkToExplorer } from '@/dbt-explorer';
import { useFavoriteAssets } from '@/hooks/admin/favoriteAssets';
import { useFavoriteProjectIds } from '@/hooks/admin/favoriteProjects';
import { Project, useProjectLazy, useProjectsLazy } from '@/hooks/admin/project';
import { getDefaultEnvironmentIds } from '@/hooks/admin/types';
import {
  useLazyProjectFile,
  useLazyProjectFiles,
} from '@/hooks/discovery/catalog/files';
import { useValidatedParams } from '@/hooks/params';
import { UrlEnvironmentType } from '@/typings/url';
import { truthy } from '@/util/array';
import { useDbtCloudAccount } from '../DbtCloudProvider';
import {
  DBT_FILES_ROOT_NODE_NAME,
  DBT_ROOT_NODE_LABEL,
  PROJECT_PAGE_SIZE,
} from './const';
import { DbtInternalTree, DbtTreeData } from './types';
import {
  generateFile,
  generateFolder,
  generateFolderWithIcon,
  GetMenuActionsData,
  scrollToPath,
  sortDbtProjects,
  useMenuActions,
} from './utils';

const rootNode = generateFolderWithIcon(DBT_FILES_ROOT_NODE_NAME, {
  iconOverride: {
    ryecon: RyeconColorDbt,
    label: DBT_ROOT_NODE_LABEL,
  },
  name: DBT_ROOT_NODE_LABEL,
  data: {
    pagination: {
      page: 0,
      hasMore: false,
      total: 0,
      isLoading: false,
    },
  },
});

const generateFileTreeItems = (
  path: string,
  files: FileAppliedStateNode[],
  getMenuActions?: (data: GetMenuActionsData) => ActionsData['actions'],
) => {
  return files.map((file) => {
    const filePath =
      file.fileType === 'file' ? file.uniqueId : file.filePath.split('/').pop();
    const id = `${path}/${filePath}`;
    if (file.fileType === 'file') {
      return generateFile(id, {
        name: file.name ?? '',
        data: {
          actions: getMenuActions?.({
            projectId: file.projectId!,
            type: 'asset',
            fileTreeId: id,
            assetId: file.uniqueId,
            assetType: file.resourceType,
            environmentId: file.environmentId.toString(),
            name: file.name ?? '',
            platform: 'dbt',
          }),
        },
      });
    }
    return generateFolderWithIcon(id, {
      name: file.name ?? '',
      data: {},
    });
  });
};

const generateProjectFileTreeItems = (
  projectData: Project[],
  selectedEnvironmentType: UrlEnvironmentType,
  getMenuActions?: (data: GetMenuActionsData) => ActionsData['actions'],
) => {
  return projectData.map((project) => {
    const id = `${DBT_FILES_ROOT_NODE_NAME}/${project.id}`;
    return generateFolderWithIcon(id, {
      name: project.name ?? '',
      data: {
        actions: getMenuActions?.({
          projectId: project.id!,
          type: 'project',
          fileTreeId: id,
          platform: 'dbt',
          name: project.name ?? '',
          environmentId:
            getDefaultEnvironmentIds(project?.environments ?? [], [
              selectedEnvironmentType,
            ])?.[0]?.toString() ?? '',
        }),
        tooltipText: project.name,
        pagination: {
          page: 0,
          hasMore: false,
          total: 0,
          isLoading: false,
        },
      },
    });
  });
};

const onSort = (a: string, b: string, allProjects: Project[]) => {
  const partsA = a.split('/').filter((part) => !!part);
  const partsB = b.split('/').filter((part) => !!part);
  if (partsA.length === partsB.length && partsA.length === 2) {
    return sortDbtProjects(a, b, allProjects);
  } else if (partsA.length !== partsB.length) {
    return partsA.length - partsB.length;
  }
  return a.localeCompare(b, undefined, {
    numeric: true,
    sensitivity: 'base',
    ignorePunctuation: true,
  });
};

export const useDbtFileTree = ({
  allProjects,
  setAllProjects,
  projectsPageInfo,
  setProjectsPageInfo,
  viewParam,
  selectedEnvironmentType,
}: DbtInternalTree): DbtTreeData => {
  const { accountId } = useDbtCloudAccount();
  const { projectId, uniqueId } = useValidatedParams();
  const [isInitialized, setIsInitialized] = useState(false);
  const prevEnvironmentTypeRef = useRef(selectedEnvironmentType);

  const derivedAssetId = useMemo(() => uniqueId?.raw, [uniqueId?.raw]);

  const navigate = useNavigate();
  const location = useLocation();
  const accountOverview = useLinkToExplorer('multiProject');
  const dbtProject = useLinkToExplorer('project');
  const resourceDetails = useLinkToExplorer('resourceDetails');

  const { favoriteProjectIds } = useFavoriteProjectIds(accountId ?? 0);
  const { data: favoriteAssets } = useFavoriteAssets(accountId ?? 0);
  const { getMenuActions } = useMenuActions();

  const [selectedFile, setSelectedFile] = useState<string>();
  const [selectedFolder, setSelectedFolder] = useState<string | undefined>(undefined);

  const [dbtTree, setDbtTree] = useState<FileTreeItemType[]>([rootNode]);

  const [, getProject] = useProjectLazy();
  const [projects, getProjects] = useProjectsLazy();

  const [, getProjectFiles] = useLazyProjectFiles();
  const [, getProjectFile] = useLazyProjectFile();

  const isActive = useMemo(() => {
    return viewParam === 'fileTree';
  }, [viewParam]);

  const { openDirectories, setOpenDirectories } = useOpenDirectories({
    items: dbtTree,
    activeFilePath: selectedFile,
    rootNodeName: DBT_FILES_ROOT_NODE_NAME,
  });

  /**
   * Update the tree from the environment
   */
  const updateTreeFromEnvironment = useCallback(async () => {
    // get the selected project id
    const selectedProjectId =
      selectedFolder?.split('/')?.[1] ?? selectedFile?.split('/')?.[1] ?? '';

    // build projects tree and set the selected project to loading
    let projectsTreeItems = allProjects.map((project) => {
      const id = `${DBT_FILES_ROOT_NODE_NAME}/${project.id}`;
      const isSelected = selectedProjectId === project?.id?.toString();
      return generateFolderWithIcon(id, {
        name: project.name ?? '',
        data: {
          tooltipText: project.name,
          pagination: {
            page: 0,
            hasMore: false,
            total: 0,
            isLoading: isSelected,
          },
        },
      });
    });

    // set the root node to not be loading
    const updatedRootNode = {
      ...rootNode,
      data: {
        ...rootNode.data,
        pagination: {
          ...rootNode.data.pagination,
          isLoading: false,
          page: rootNode.data.pagination?.page ?? 0,
          total: rootNode.data.pagination?.total ?? 0,
          hasMore: projectsPageInfo.hasNextPage,
        },
      },
    };

    setDbtTree([updatedRootNode, ...projectsTreeItems]);

    // if there is a selected project, fetch the files
    if (selectedProjectId) {
      const projectPath = `${DBT_FILES_ROOT_NODE_NAME}/${selectedProjectId}`;
      const projectQuery = await getProjectFiles({
        environmentId:
          getDefaultEnvironmentIds(
            allProjects.find((p) => p.id === Number(selectedProjectId))?.environments ||
              [],
            [selectedEnvironmentType],
          )?.[0] ?? 0,
        path: '',
        uniqueId: '',
        after: '',
      });

      const projectFiles = projectQuery.data ?? [];

      // generate the project tree items
      const projectTreeItems = generateFileTreeItems(
        projectPath,
        projectFiles as FileAppliedStateNode[],
        getMenuActions,
      );

      // update the page info
      if (projectQuery.pageInfo?.hasNextPage) {
        setProjectsPageInfo((prev) => ({
          ...prev,
          after: {
            ...prev.after,
            [projectPath]: projectQuery.pageInfo?.endCursor || '',
          },
        }));
      }

      // update the selected project to not be loading
      projectsTreeItems = projectsTreeItems.map((item) => {
        if (item.id === projectPath) {
          return {
            ...item,
            data: {
              ...item.data,
              pagination: {
                ...item.data.pagination,
                isLoading: false,
                page: 0,
                total: 0,
                hasMore: projectQuery.pageInfo?.hasNextPage ?? false,
              },
            },
          };
        }
        return item;
      });

      // update the tree and open directories
      setDbtTree([updatedRootNode, ...projectsTreeItems, ...projectTreeItems]);
      setOpenDirectories([DBT_FILES_ROOT_NODE_NAME, projectPath].filter(Boolean));
      setSelectedFolder(projectPath);
    } else {
      setOpenDirectories([DBT_FILES_ROOT_NODE_NAME]);
      setSelectedFolder(undefined);
      setSelectedFile(undefined);
    }
  }, [
    allProjects,
    getMenuActions,
    getProjectFiles,
    projectsPageInfo.hasNextPage,
    selectedEnvironmentType,
    selectedFile,
    selectedFolder,
    setOpenDirectories,
    setProjectsPageInfo,
  ]);

  const buildFileTreeData = useCallback(async () => {
    const fileTreeItems: FileTreeItemType[] = [];
    const openDirectories: string[] = [DBT_FILES_ROOT_NODE_NAME];
    const afterCursors: Record<string, string> = {};
    // if we don't have a project id or unique id, we just build the tree with the projects
    if (!projectId && !uniqueId?.raw) {
      fileTreeItems.push(
        ...generateProjectFileTreeItems(
          allProjects,
          selectedEnvironmentType,
          getMenuActions,
        ),
      );
    } else {
      // get the project data
      let project = allProjects.find((p) => p.id === Number(projectId));
      if (!project) {
        const projectQuery = await getProject({
          accountId: accountId ?? 0,
          projectId: Number(projectId),
          includeRelated: ['environments'],
        });
        if (!projectQuery.data) return { fileTreeItems, openDirectories, afterCursors };
        project = projectQuery.data;
      }

      // generate the projects tree items (all projects + the project we are building the tree for)
      const projectsFileTreeItems = generateProjectFileTreeItems(
        uniqBy([...allProjects, project], 'id'),
        selectedEnvironmentType,
        getMenuActions,
      );

      // add the projects tree items to the file tree
      fileTreeItems.push(...projectsFileTreeItems);

      const projectPath = `${DBT_FILES_ROOT_NODE_NAME}/${project.id}`;

      // get the environment id
      const environmentId = getDefaultEnvironmentIds(project.environments || [], [
        selectedEnvironmentType,
      ])?.[0];
      if (!environmentId) {
        setAllProjects((prev) => uniqBy([...prev, project], 'id').filter(truthy));
        setSelectedFolder(projectPath);
        openDirectories.push(projectPath);
        return { fileTreeItems, openDirectories, afterCursors };
      }

      // fetch the project files (first page)
      const projectFilesQuery = await getProjectFiles({
        environmentId,
        path: '',
        uniqueId: '',
        after: '',
      });

      const projectFiles = projectFilesQuery.data;
      if (!projectFiles) return { fileTreeItems, openDirectories, afterCursors };

      // add the after cursors to the after cursors object
      afterCursors[projectPath] = projectFilesQuery.pageInfo?.endCursor || '';

      // generate the project file tree items
      const projectFileTreeItems = generateFileTreeItems(
        projectPath,
        projectFiles as FileAppliedStateNode[],
        getMenuActions,
      );

      // update the project file tree item with the pagination info
      const projectFileTreeItem = fileTreeItems.find((item) => item.id === projectPath);
      if (projectFileTreeItem) {
        projectFileTreeItem.data.pagination = {
          page: 0,
          hasMore: projectFilesQuery.pageInfo?.hasNextPage ?? false,
          total: 0,
          isLoading: false,
        };
      }

      // add the project file tree items to the file tree
      fileTreeItems.push(...projectFileTreeItems);

      // if we don't have a unique id, we just add the project to the all projects and return the file tree items and open directories
      if (!uniqueId?.raw) {
        setAllProjects((prev) => uniqBy([...prev, project], 'id').filter(truthy));
        setSelectedFolder(projectPath);
        openDirectories.push(projectPath);
        return { fileTreeItems, openDirectories, afterCursors };
      }

      // fetch the file from the unique id
      const fileQuery = await getProjectFile({
        environmentId,
        uniqueId: uniqueId?.raw ?? '',
        after: '',
      });

      const file = fileQuery.data;
      if (!file) return { fileTreeItems, openDirectories, afterCursors };

      // build the path from the file in a combination of always adding the next one. e.g a, a/b, a/b/c, etc
      const allPaths = file.filePath
        .split('/')
        .slice(0, -1)
        .map((_, index, paths) => {
          return [...paths.slice(0, index + 1)].join('/');
        });

      // for each path, we fetch the files and add them to the file tree
      for (const path of allPaths) {
        const pathWithProject = `${DBT_FILES_ROOT_NODE_NAME}/${project.id}/${path}`;
        const fileTreeItem = fileTreeItems.find((item) => item.id === pathWithProject);

        const fileQuery = await getProjectFiles({
          environmentId,
          path,
          uniqueId: '',
          after: '',
        });

        // if we don't have any files, we stop
        if (!fileQuery.data) break;

        // add the after cursors to the after cursors object
        afterCursors[pathWithProject] = fileQuery.pageInfo?.endCursor || '';

        // if the folder is not present in the file tree, we add it, otherwise we update the pagination info
        if (!fileTreeItem) {
          fileTreeItems.push(
            generateFolder(pathWithProject, {
              name: path.split('/').pop() ?? '',
              data: {
                pagination: {
                  page: 0,
                  hasMore: fileQuery.pageInfo?.hasNextPage ?? false,
                  total: 0,
                  isLoading: false,
                },
              },
            }),
          );
        } else {
          fileTreeItem.data.pagination = {
            page: 0,
            hasMore: fileQuery.pageInfo?.hasNextPage ?? false,
            total: 0,
            isLoading: false,
          };
        }

        // if we have files, we add them to the file tree
        if (fileQuery.data) {
          fileTreeItems.push(
            ...generateFileTreeItems(
              pathWithProject,
              fileQuery.data as FileAppliedStateNode[],
              getMenuActions,
            ),
          );
        }
      }

      // the active path is the last path in the all paths array
      const activePath = allPaths[allPaths.length - 1];

      // if the file is not present in the file tree, we add it
      if (
        !fileTreeItems.find(
          (item) => item.id === `${projectPath}/${activePath}/${file.uniqueId}`,
        )
      ) {
        fileTreeItems.push(
          ...generateFileTreeItems(
            `${projectPath}/${activePath}`,
            [file] as FileAppliedStateNode[],
            getMenuActions,
          ),
        );
      }

      // set the selected file
      setSelectedFile(`${projectPath}/${activePath}/${file.uniqueId}`);
      setTimeout(() => {
        scrollToPath(`${projectPath}/${activePath}/${file.uniqueId}`);
      }, 500);

      // add the project to the all projects
      setAllProjects((prev) => uniqBy([...prev, project], 'id').filter(truthy));

      // add the project and the paths to the open directories
      openDirectories.push(
        projectPath,
        ...allPaths.map((path) => `${projectPath}/${path}`),
      );
    }
    return { fileTreeItems, openDirectories, afterCursors };
  }, [
    accountId,
    allProjects,
    getMenuActions,
    getProject,
    getProjectFile,
    getProjectFiles,
    projectId,
    selectedEnvironmentType,
    setAllProjects,
    uniqueId?.raw,
  ]);

  /**
   * Initializes the tree. If we have a project id or unique id, we build the whole tree
   * Otherwise, we build the tree only with the projects
   */
  const buildTree = useCallback(async () => {
    // get the project file tree items and the open directories
    const {
      fileTreeItems: projectFileTreeItems,
      openDirectories: projectOpenDirectories,
      afterCursors: projectAfterCursors,
    } = await buildFileTreeData();

    // update the tree
    setDbtTree((prev) => {
      const newTree = [...prev, ...projectFileTreeItems];
      const updatedNode = newTree.find((node) => node.id === DBT_FILES_ROOT_NODE_NAME);
      if (!updatedNode) return prev;
      updatedNode.data.pagination = {
        hasMore: projectsPageInfo.hasNextPage,
        page: 0,
        total: projectsPageInfo.totalCount,
        isLoading: false,
      };
      return uniqBy(newTree, 'id');
    });

    // set the open directories
    setOpenDirectories(projectOpenDirectories.filter(Boolean));

    // set the after cursors
    setProjectsPageInfo((prev) => ({
      ...prev,
      after: {
        ...prev.after,
        ...projectAfterCursors,
      },
    }));

    // set the initialized state
    setIsInitialized(true);
  }, [
    buildFileTreeData,
    projectsPageInfo.hasNextPage,
    projectsPageInfo.totalCount,
    setOpenDirectories,
    setProjectsPageInfo,
  ]);

  // when the environment type changes, we need to reset the tree
  useEffect(() => {
    if (!isActive) return;
    if (prevEnvironmentTypeRef.current !== selectedEnvironmentType) {
      prevEnvironmentTypeRef.current = selectedEnvironmentType;
      updateTreeFromEnvironment();
    }
  }, [selectedEnvironmentType, isActive, updateTreeFromEnvironment]);

  const setTreeStateForPath = useCallback(
    (path: string, props: Partial<TreeNodeValues['pagination']>) => {
      setDbtTree((prev) => {
        const newTree = [...prev];
        const updatedNode = newTree.find((node) => node.id === path);
        if (!updatedNode) return prev;
        updatedNode.data.pagination = {
          hasMore: props?.hasMore ?? updatedNode.data.pagination?.hasMore ?? false,
          page: props?.page ?? updatedNode.data.pagination?.page ?? 0,
          total: props?.total ?? updatedNode.data.pagination?.total ?? 0,
          isLoading:
            props?.isLoading ?? updatedNode.data.pagination?.isLoading ?? false,
        };
        return uniqBy(newTree, 'id');
      });
    },
    [],
  );

  const navigateToProject = useCallback(
    (folder: string | undefined) => {
      if (!folder) return;
      if (folder === DBT_FILES_ROOT_NODE_NAME) {
        navigate(
          accountOverview({
            accountId: accountId ?? 0,
          }).concat(location.search),
        );
        return;
      }
      const parts = folder.split('/');
      if (parts.length < 2) return;
      const projectId = parts.pop();
      navigate(
        dbtProject({
          accountId: accountId ?? 0,
          projectId: Number(projectId),
          environmentType: selectedEnvironmentType,
        }).concat(location.search),
      );
    },
    [
      navigate,
      dbtProject,
      accountId,
      selectedEnvironmentType,
      location.search,
      accountOverview,
    ],
  );

  // update the tree with the favorite actions
  useEffect(() => {
    if (isActive && isInitialized) {
      setDbtTree((prev) => {
        const newTree = prev.map((node) => {
          // it could be a project or a resource
          const assetId = node.id.split('/').pop();
          const project = allProjects.find((project) => project.id === Number(assetId));
          const asset = favoriteAssets?.find((asset) => asset.asset_id === assetId);
          if (!project && !asset) return node;

          return {
            ...node,
            data: {
              ...node.data,
              actions: getMenuActions?.({
                projectId: project?.id ?? asset?.project_id ?? undefined,
                type: project ? 'project' : 'asset',
                fileTreeId: node.id,
                assetId: asset?.asset_id ?? assetId,
                environmentId:
                  asset?.environment_id ??
                  getDefaultEnvironmentIds(project?.environments ?? [], [
                    selectedEnvironmentType,
                  ])?.[0]?.toString() ??
                  '',
                name: asset?.name ?? project?.name ?? '',
                assetType: asset?.asset_type ?? '',
                platform: asset?.platform ?? 'dbt',
              }),
            },
          };
        });
        return newTree;
      });
    }
    // we only need to check for the favorite projects/assets and the active state
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favoriteProjectIds, favoriteAssets, isActive, isInitialized]);

  const updateProjectsTree = useCallback(
    (
      currentData: {
        data: Project[];
        pageInfo?: { hasNextPage?: boolean; endCursor?: string; totalCount?: number };
      },
      path: string,
    ) => {
      if (!currentData || !currentData.data) return;

      let fileTreeItems: FileTreeItemType[] = [];
      if (path === DBT_FILES_ROOT_NODE_NAME) {
        const projectData = currentData.data as Project[];
        fileTreeItems = generateProjectFileTreeItems(
          projectData,
          selectedEnvironmentType,
          getMenuActions,
        );
      }

      if (currentData.pageInfo?.hasNextPage) {
        setProjectsPageInfo((prev) => ({
          ...prev,
          hasNextPage: currentData.pageInfo?.hasNextPage ?? false,
          totalCount: currentData.pageInfo?.totalCount ?? 0,
          after: {
            ...prev.after,
            [path]: currentData.pageInfo?.endCursor || '',
          },
        }));
      }

      setDbtTree((prev) => {
        const newItems = [...prev, ...fileTreeItems];
        const updatedNode = newItems.find((node) => node.id === path);
        if (!updatedNode) return prev;
        const page = updatedNode.data.pagination?.page ?? 0;
        return uniqBy(
          [
            ...prev.filter((node) => node.id !== path),
            ...fileTreeItems,
            {
              ...updatedNode,
              data: {
                ...updatedNode.data,
                pagination: {
                  ...updatedNode.data.pagination,
                  hasMore: currentData.pageInfo?.hasNextPage ?? false,
                  total: currentData.pageInfo?.totalCount ?? 0,
                  isLoading: false,
                  page: page,
                },
              },
            },
          ],
          'id',
        );
      });
    },
    [getMenuActions, selectedEnvironmentType, setProjectsPageInfo],
  );

  const updateFileTree = useCallback(
    (
      currentData: {
        data: FileAppliedStateNode[];
        pageInfo?: { hasNextPage?: boolean; endCursor?: string; totalCount?: number };
      },
      path: string,
    ) => {
      const fileTreeItems = generateFileTreeItems(
        path,
        currentData.data,
        getMenuActions,
      );

      if (currentData.pageInfo?.hasNextPage) {
        setProjectsPageInfo((prev) => ({
          ...prev,
          after: {
            ...prev.after,
            [path]: currentData.pageInfo?.endCursor || '',
          },
        }));
      }

      setDbtTree((prev) => {
        const newItems = [...prev, ...fileTreeItems];
        const updatedNode = newItems.find((node) => node.id === path);
        if (!updatedNode) return prev;
        const page = updatedNode.data.pagination?.page ?? 0;
        return uniqBy(
          [
            ...prev.filter((node) => node.id !== path),
            ...fileTreeItems,
            {
              ...updatedNode,
              data: {
                ...updatedNode.data,
                pagination: {
                  ...updatedNode.data.pagination,
                  hasMore: currentData.pageInfo?.hasNextPage ?? false,
                  total: currentData.pageInfo?.totalCount ?? 0,
                  isLoading: false,
                  page: page,
                },
              },
            },
          ],
          'id',
        );
      });
    },
    [getMenuActions, setProjectsPageInfo],
  );

  const generateChildrenForPath = useCallback(
    async (path: string): Promise<void> => {
      setTreeStateForPath(path, { isLoading: true, hasMore: false });

      if (path === DBT_FILES_ROOT_NODE_NAME) {
        const projectsQuery = await getProjects({
          accountId: accountId ?? 0,
          first: PROJECT_PAGE_SIZE,
          after: projectsPageInfo.after[path] || '',
          includeRelated: ['environments'],
        });
        if (!projectsQuery.data) {
          setTreeStateForPath(path, { isLoading: false, hasMore: false });
          return;
        }
        setAllProjects((prev) =>
          uniqBy([...prev, ...(projectsQuery.data as Project[])], 'id'),
        );
        setProjectsPageInfo((prev) => ({
          ...prev,
          hasNextPage: projectsQuery.pageInfo?.hasNextPage ?? false,
          totalCount: projectsQuery.pageInfo?.totalCount ?? 0,
          after: {
            ...prev.after,
            [DBT_ROOT_NODE_LABEL]: projectsQuery.pageInfo?.endCursor || '',
          },
        }));
        updateProjectsTree(
          {
            data: projectsQuery.data as Project[],
            pageInfo: projectsQuery.pageInfo,
          },
          path,
        );
      } else if (path.startsWith(`${DBT_FILES_ROOT_NODE_NAME}/`)) {
        const pathParts = path.split('/');
        const project = allProjects.find((p) => p.id === Number(pathParts[1]));
        if (!project) {
          setTreeStateForPath(path, { isLoading: false, hasMore: false });
          return;
        }
        const environmentIds = getDefaultEnvironmentIds(project.environments || [], [
          selectedEnvironmentType,
        ]);
        if (!environmentIds) {
          // get the parent node
          setTreeStateForPath(path, { isLoading: false, hasMore: false });
          return;
        }

        const filesQuery = await getProjectFiles({
          environmentId: environmentIds[0],
          path: pathParts.length === 2 ? '' : pathParts.slice(2).join('/'),
          uniqueId: '',
          after: projectsPageInfo.after[path] || '',
        });

        if (filesQuery.data?.length === 0) {
          setTreeStateForPath(path, { isLoading: false, hasMore: false });
          return;
        }

        updateFileTree(
          {
            data: (filesQuery.data as FileAppliedStateNode[]) ?? [],
            pageInfo: {
              hasNextPage: filesQuery.pageInfo?.hasNextPage ?? false,
              endCursor: filesQuery.pageInfo?.endCursor ?? '',
            },
          },
          path,
        );
      }
    },
    [
      setTreeStateForPath,
      getProjects,
      accountId,
      projectsPageInfo.after,
      setAllProjects,
      setProjectsPageInfo,
      updateProjectsTree,
      allProjects,
      selectedEnvironmentType,
      getProjectFiles,
      updateFileTree,
    ],
  );

  const handleSetSelectedFile = useCallback(
    (value: SetStateAction<string | undefined>) => {
      const newValue = typeof value === 'function' ? value(selectedFile) : value;
      setSelectedFile(newValue);
      const resourceId = newValue?.split('/').pop();
      const inferredProjectId = newValue?.split('/')?.[1];
      if (!resourceId || !inferredProjectId) return;
      navigate(
        resourceDetails({
          accountId: accountId ?? 0,
          dbtUniqueId: resourceId,
          projectId: Number(inferredProjectId),
          environmentType: selectedEnvironmentType,
        }).concat(location.search),
      );
    },
    [
      selectedFile,
      navigate,
      resourceDetails,
      accountId,
      selectedEnvironmentType,
      location.search,
    ],
  );

  const handleSetSelectedFolder = useCallback(
    (
      value: SetStateAction<string | undefined>,
      _event?: React.MouseEvent,
      isCollapseClick = false,
    ) => {
      const newValue = typeof value === 'function' ? value(selectedFolder) : value;
      if (!isCollapseClick) {
        const isProject = newValue?.split('/').length === 2;
        const isRoot = newValue?.split('/').length === 1;
        setSelectedFolder(newValue);
        if (isProject || isRoot) {
          navigateToProject(newValue);
        }
      }
    },
    [navigateToProject, selectedFolder],
  );

  // when the view param changes, we need to update the tree and selected file or folder
  useEffect(() => {
    if (isActive && isInitialized) {
      buildTree();
    } else if (!isActive) {
      setSelectedFolder(undefined);
      setSelectedFile(undefined);
    }
    // we only need to reload the tree when the view param changes
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, derivedAssetId]);

  // initialize the tree
  useEffect(() => {
    if (isActive && allProjects.length > 0 && !isInitialized) {
      buildTree();
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, allProjects.length, isInitialized]);

  return {
    fileTree: dbtTree,
    setFileTree: setDbtTree,
    isLoading: projects.fetching || !isInitialized,
    initialized: isInitialized,
    generateChildrenForPath,
    openDirectories,
    setOpenDirectories,
    selectedFile,
    setSelectedFile: handleSetSelectedFile,
    selectedFolder,
    setSelectedFolder: handleSetSelectedFolder,
    onSort: (a: string, b: string) => onSort(a, b, allProjects),
  };
};
